"use client";

import { JSX, useMemo, useState } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useTween } from "@/lib/hooks/use-tween";
import { useHoverCapable } from "@/lib/hooks/use-hover-capable";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import { SegmentTabs } from "@/components/motion/segment-tabs";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  STAGE_BARS_MAX_STAGES,
  type ModuleStageBarsData,
  type ModuleStageRange,
} from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";
import { formatModuleValue, formatShare } from "./shared/value-format";

const STAGE_COLOR = "var(--chart-1)";
const MIN_STAGE_OPACITY = 0.4;

type ResolvedStage = {
  key: string;
  name: string;
  value: number;
  share: number;
  step: number;
  color: string;
};

function resolveStages(range: ModuleStageRange | undefined): ResolvedStage[] {
  const raw = (range?.stages ?? []).slice(0, STAGE_BARS_MAX_STAGES);
  const first = raw[0];
  const span = Math.max(raw.length - 1, 1);
  return raw.map((s, i) => {
    const prev = raw[i - 1];
    const strength = Math.round((1 - i * ((1 - MIN_STAGE_OPACITY) / span)) * 100);
    return {
      key: s.id ?? String(i),
      name: stripInlineMarkdown(s.name),
      value: s.value,
      share: first && first.value > 0 ? Math.min(1, s.value / first.value) : 0,
      step: prev ? (prev.value > 0 ? s.value / prev.value : 0) : 1,
      color: `color-mix(in oklab, ${STAGE_COLOR} ${strength}%, transparent)`,
    };
  });
}

export default function ModuleStageBars({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleStageBarsData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, intlLocale } = useViewerLanguage();
  const ranges = data?.ranges ?? [];
  const format = data?.format ?? "number";
  const label = stripInlineMarkdown(data?.label);
  const legendTitle =
    stripInlineMarkdown(data?.legendTitle) || t("Conversion from previous stage");
  const keyOf = (i: number) => ranges[i]?.id ?? String(i);

  const [range, setRange] = useState<string>(keyOf(0));
  const currentIndex = Math.max(
    0,
    ranges.findIndex((_, i) => keyOf(i) === range),
  );

  const resolved = useMemo(() => ranges.map((r) => resolveStages(r)), [ranges]);
  const stages = resolved[currentIndex] ?? [];

  const stage = useLegendHoverSelect();
  const hasHover = useHoverCapable();
  const active = stage.effective !== null && stage.effective < stages.length ? stage.effective : null;
  const headline = active !== null ? stages[active] : stages[0];
  const shown = useTween(headline?.value ?? 0, {
    from: 0,
    duration: active === null ? 800 : 300,
  });

  const caption = (list: ResolvedStage[], index: number | null) => {
    if (!list.length) return null;
    if (index === null || index === 0) {
      return (
        <span className="truncate text-[var(--muted)]">
          {t("{stage}, starting volume", { stage: list[0].name })}
        </span>
      );
    }
    const s = list[index];
    const [before, after = ""] = t("{stage}, {share} of {first}", {
      stage: s.name,
      first: list[0].name,
    }).split("{share}");
    return (
      <span className="truncate text-[var(--muted)]">
        {before}
        <span className="font-medium tabular-nums text-[var(--fg)]">
          {formatShare(s.share, 0, intlLocale)}
        </span>
        {after}
      </span>
    );
  };

  const renderRows = (list: ResolvedStage[], live: boolean, i: number) => (
    <div
      key={keyOf(i)}
      role={live ? "list" : undefined}
      aria-hidden={live ? undefined : true}
      className={cn(
        "grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_auto] gap-y-1 [grid-area:1/1]",
        !live && "pointer-events-none invisible",
      )}
    >
      {list.map((s, j) => {
        const dim = live && active !== null && active !== j;
        return (
          <button
            key={s.key}
            type="button"
            role={live ? "listitem" : undefined}
            tabIndex={live ? 0 : -1}
            aria-pressed={live ? stage.selected === j : undefined}
            className={cn(
              "col-span-3 -mx-1 grid grid-cols-subgrid items-center gap-x-3 rounded-md px-1 py-1 text-left transition-opacity duration-200",
              dim && "opacity-40",
            )}
            onMouseEnter={live && hasHover ? () => stage.setHovered(j) : undefined}
            onMouseLeave={live && hasHover ? () => stage.setHovered(null) : undefined}
            onFocus={live && hasHover ? () => stage.setHovered(j) : undefined}
            onBlur={live && hasHover ? () => stage.setHovered(null) : undefined}
            onClick={
              live
                ? (e) =>
                    stage.select(j, {
                      kind: "row",
                      id: s.key,
                      index: j,
                      element: e.currentTarget,
                    })
                : undefined
            }
          >
            <span className="truncate text-right text-sm text-[var(--muted)]">{s.name}</span>
            <span className="relative block h-1.5 overflow-hidden rounded-full bg-[var(--track)]">
              <span
                className="block h-full rounded-full transition-[width] duration-500 ease-out"
                style={{
                  width: `${Math.round(s.share * 1000) / 10}%`,
                  minWidth: 4,
                  background: s.color,
                }}
              />
            </span>
            <span className="flex items-baseline justify-end gap-2 text-sm tabular-nums">
              <span className="font-medium text-[var(--fg)]">
                {formatModuleValue(s.value, { format, locale: intlLocale })}
              </span>
              <span className="w-10 text-right text-xs text-[var(--subtle)]">
                {formatShare(s.share, 0, intlLocale)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  const renderLegend = (list: ResolvedStage[], live: boolean, i: number) => (
    <div
      key={keyOf(i)}
      aria-hidden={live ? undefined : true}
      className={cn("min-w-0 [grid-area:1/1]", !live && "pointer-events-none invisible")}
    >
      <div className="mb-1 px-2 text-xs font-normal text-[var(--subtle)]">{legendTitle}</div>
      <Legend
        items={list.map((s, j) => ({
          id: s.key,
          label: s.name,
          color: s.color,
          value: formatShare(j === 0 ? 1 : s.step, 0, intlLocale),
        }))}
        hoveredIndex={live ? stage.hovered : null}
        onHoverChange={live ? stage.setHovered : () => undefined}
        selectedIndex={live ? stage.selected : null}
        onSelect={live ? (j) => stage.select(j) : undefined}
        onLabelRef={live ? stage.setLabelRef : undefined}
        className="flex snap-x flex-row flex-nowrap gap-1 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <LegendItem className="flex shrink-0 snap-start items-center gap-2 px-2 py-1">
          <LegendMarker />
          <LegendLabel className="max-w-none whitespace-nowrap text-[13px] font-medium text-[var(--fg)]" />
          <LegendValue className="whitespace-nowrap text-xs tabular-nums" />
        </LegendItem>
      </Legend>
    </div>
  );

  return (
    <div
      className={cn("flex w-full flex-col gap-5", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {label && <div className="truncate text-sm text-[var(--muted)]">{label}</div>}
          <div className="mt-1 truncate text-3xl font-medium tracking-tight tabular-nums text-[var(--fg)]">
            {formatModuleValue(Math.round(shown), { format, locale: intlLocale })}
          </div>
          <div className="mt-1 flex h-5 min-w-0 items-center text-sm">
            {caption(stages, active)}
          </div>
        </div>
        {ranges.length > 1 && (
          <SegmentTabs
            value={keyOf(currentIndex)}
            onValueChange={(v) => {
              if (v === null) return;
              setRange(v);
              stage.setHovered(null);
              stage.setSelected(null);
            }}
            options={ranges.map((r, i) => ({
              value: keyOf(i),
              label: stripInlineMarkdown(r.label),
            }))}
          />
        )}
      </div>

      <div className="grid">
        {resolved.map((list, i) => renderRows(list, i === currentIndex, i))}
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] border-t border-dashed border-[var(--border-dashed-soft)] pt-3">
        {resolved.map((list, i) => renderLegend(list, i === currentIndex, i))}
      </div>
    </div>
  );
}
